import React from 'react';
import { useCMS } from '../../context/CMSContext';
import { IconRenderer } from '../common/IconRenderer';
import { BannerMediaRenderer } from '../common/BannerMediaRenderer';
import { getComputedTextStyle } from '../../utils/typographyHelper';
import { ArrowLinkText } from '../common/ArrowLinkText';
import { getBannerOverlayGradient } from '../../utils/overlayUtils';

export const HeroSection: React.FC = () => {
  const { data, setIsProspectusModalOpen } = useCMS();
  const { hero, typography, siteSettings } = data;

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const overlayGradient = getBannerOverlayGradient({
    overlayDarkness: hero.overlayDarkness ?? hero.overlayOpacity ?? siteSettings.globalBannerOverlayDarkness ?? 100,
    overlayColor: hero.overlayColor || siteSettings.globalBannerOverlayColor || '#000000'
  });
  const globalWeight = typography?.allHeadingFontWeight || '300';
  const headingWeight = hero.titleStyle?.fontWeight || hero.headlineFontWeight || globalWeight;
  const subheadlineWeight = hero.subtitleStyle?.fontWeight || hero.subheadlineFontWeight || '300';

  const computedHeroHeading = getComputedTextStyle(
    hero.titleStyle,
    typography?.heroHeadline,
    {
      fontSize: hero.titleStyle?.fontSize || hero.headlineFontSize || 68,
      fontWeight: headingWeight,
      fontColor: hero.titleStyle?.fontColor || typography?.heroHeadline?.fontColor || '#f5f2eb',
      lineHeight: hero.titleStyle?.lineHeight || 1.12,
      letterSpacing: hero.titleStyle?.letterSpacing || '-0.02em',
      fontFamily: hero.titleStyle?.fontFamily || 'jost',
      textTransform: hero.titleStyle?.textTransform || 'none'
    }
  );

  const computedHeroSubheadline = getComputedTextStyle(
    hero.subtitleStyle,
    typography?.heroSubheadline,
    {
      fontSize: hero.subtitleStyle?.fontSize || hero.subheadlineFontSize || 15,
      fontWeight: subheadlineWeight,
      fontColor: hero.subtitleStyle?.fontColor || typography?.heroSubheadline?.fontColor || '#d6d4ce',
      lineHeight: hero.subtitleStyle?.lineHeight || 1.6,
      letterSpacing: hero.subtitleStyle?.letterSpacing || 'normal',
      fontFamily: hero.subtitleStyle?.fontFamily || 'jost',
      textTransform: hero.subtitleStyle?.textTransform || 'none'
    }
  );

  const computedFeatureBadgeTitleStyle = getComputedTextStyle(
    hero.featureBadgeStyle,
    typography?.featureBadgeTitle,
    {
      fontSize: 11,
      fontWeight: '500',
      fontColor: '#d6d4ce',
      fontFamily: 'jost',
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      textDecoration: 'none',
      lineHeight: 1.25
    }
  );

  const commonIconSize = hero.featureBadgeIconSize || 26;
  const badgeAlignment = (computedFeatureBadgeTitleStyle.textAlign as 'left' | 'center' | 'right') || 'center';
  const alignClass = badgeAlignment === 'left' ? 'items-start' : badgeAlignment === 'right' ? 'items-end' : 'items-center text-center';

  return (
    <section id="home" className="relative min-h-screen flex flex-col justify-between pt-20 md:pt-24 lg:pt-28 pb-10 md:pb-12 bg-[#121417] text-white overflow-hidden">
      {/* Background Media (Video or Photography) with Atmospheric Overlay Darkness */}
      <BannerMediaRenderer
        bgType={hero.bgType}
        bgImageUrl={hero.bgImageUrl}
        bgVideoUrl={hero.bgVideoUrl}
        overlayGradient={overlayGradient}
        overlayDarkness={hero.overlayDarkness ?? hero.overlayOpacity ?? siteSettings.globalBannerOverlayDarkness ?? 100}
        overlayColor={hero.overlayColor || siteSettings.globalBannerOverlayColor || '#000000'}
        videoAutoplay={hero.videoAutoplay ?? true}
        videoMuted={hero.videoMuted ?? true}
        videoLoop={hero.videoLoop ?? true}
        imgAlt="Oltraya Estate Panoramic View"
      />

      {/* Main Hero Headline Content - Vertically Centered */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full my-auto py-6">
        <div className="max-w-3xl space-y-4 md:space-y-5">
          {/* Main Title with Jost font and controllable size/weight */}
          <h1
            className="font-jost tracking-tight leading-[1.12] animate-luxury-fade-up delay-100"
            style={computedHeroHeading}
          >
            <span>{hero.headlinePart1 || 'Anyone can sell you a view.'}</span>
            <br />
            <span>{hero.headlinePart2 || 'We sell you'} </span>
            <span className="italic text-[#cbb08b] gold-glow" style={{ fontWeight: computedHeroHeading.fontWeight }}>
              {hero.headlineHighlight || 'Peace.'}
            </span>
          </h1>

          {/* Subheadline matching screenshot */}
          <p
            className="font-jost tracking-wide leading-relaxed max-w-xl text-[#d2d0cb] animate-luxury-fade-up delay-100"
            style={computedHeroSubheadline}
          >
            {hero.subheadlineLine1} {hero.subheadlineLine2}
          </p>

          {/* CTA Button matching screenshot (outline transparent button with #f7f2e8 border and thin typography) */}
          {Boolean(hero.ctaText && hero.ctaText.trim()) && (
            <div className="pt-1 md:pt-2 animate-luxury-fade-up delay-400">
              <button
                onClick={() => setIsProspectusModalOpen(true)}
                className="px-7 sm:px-8 py-3 sm:py-3.5 border border-[#f7f2e8] bg-transparent hover:bg-[#f7f2e8] hover:text-[#121417] text-[#f7f2e8] font-sans text-xs sm:text-[13px] font-light tracking-[0.06em] transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg active:scale-[0.98] rounded-xs"
              >
                {hero.ctaText}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Divider line & 4 Bottom Feature Badges matching screenshot */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-6 md:pt-8 animate-luxury-fade-up delay-500">
        {/* Thin Horizontal Divider Line from screenshot */}
        <div className="w-full max-w-xl border-t border-white/20 mb-5 sm:mb-6" />

        {/* 4 Feature Icons & Multi-line Labels matching screenshot */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 sm:gap-6 max-w-2xl pb-4">
          {(hero.featurePills || []).map((pill, idx) => (
            <div
              key={pill.id || idx}
              className={`group flex flex-col ${alignClass} transition-transform duration-200 hover:-translate-y-0.5`}
              style={{ animationDelay: `${550 + idx * 75}ms` }}
            >
              <div className="text-[#a88345] mb-2.5 group-hover:scale-110 transition-transform duration-300 flex items-center justify-center min-h-[28px] w-full">
                <IconRenderer
                  name={pill.icon || pill.iconName}
                  customIconUrl={pill.iconType === 'custom' ? pill.customIconUrl : (pill.customIconUrl && !pill.icon ? pill.customIconUrl : undefined)}
                  size={commonIconSize}
                  strokeWidth={1.3}
                  className="text-[#a88345]"
                />
              </div>
              <span
                className={`block font-sans leading-tight whitespace-pre-line group-hover:text-[#a88345] transition-colors w-full ${badgeAlignment === 'center' ? 'text-center' : badgeAlignment === 'right' ? 'text-right' : 'text-left'}`}
                style={computedFeatureBadgeTitleStyle}
              >
                {pill.title || pill.text}
              </span>
            </div>
          ))}
        </div>

        {/* Explore Our Projects Arrow Link matching screenshot */}
        <div className="pt-1 sm:pt-2">
          <button
            onClick={() => scrollToSection('projects')}
            className="inline-flex items-center text-xs tracking-[0.25em] text-[#a88345] uppercase font-semibold group cursor-pointer font-sans"
          >
            <ArrowLinkText text={hero.exploreLinkText} defaultText="EXPLORE OUR PROJECTS" />
          </button>
        </div>
      </div>
    </section>
  );
};

